import {
  createSnapshotStream,
  type LiveQuote,
  type SnapshotStream,
} from "~/v3-poc/api/snapshotStream";
import { MAX_SYMBOLS } from "~/v3-poc/api/snapshotStream.constants";
import { debugKernel } from "../debug";

type QuoteHandler = (quotes: Record<string, LiveQuote>) => void;

type CreateStream = (opts: {
  getToken: () => Promise<string>;
  webSocketURL: string;
}) => SnapshotStream;

export interface SocketAdapter {
  open: (url: string, getToken: () => Promise<string>) => void;
  close: () => void;
  setSymbols: (symbols: readonly string[]) => void;
  onQuotes: (handler: QuoteHandler) => void;
}

export interface CreateSocketAdapterOptions {
  createStream?: CreateStream;
  warn?: (message: string) => void;
}

export const createSocketAdapter = ({
  createStream = createSnapshotStream,
  warn = (message) => console.warn(message),
}: CreateSocketAdapterOptions = {}): SocketAdapter => {
  let stream: SnapshotStream | null = null;
  let unsubscribe: (() => void) | null = null;
  let handler: QuoteHandler | null = null;
  let symbols: string[] = [];

  const cap = (next: readonly string[]): string[] => {
    if (next.length <= MAX_SYMBOLS) {
      return [...next];
    }
    const dropped = next.slice(MAX_SYMBOLS);
    warn(
      `[atd] market data subscription capped at ${MAX_SYMBOLS} symbols; dropped ${dropped.length}: ${dropped.join(", ")}`,
    );
    return next.slice(0, MAX_SYMBOLS);
  };

  const open = (url: string, getToken: () => Promise<string>) => {
    debugKernel("socket opening", { url, symbols });
    const next = createStream({ getToken, webSocketURL: url });
    stream = next;
    unsubscribe = next.onQuotes((quotes) => {
      debugKernel("socket received from wire", { quotes });
      handler?.(quotes);
    });
    next.setSymbols(symbols);
    next.start();
  };

  const close = () => {
    debugKernel("socket closing", { symbols });
    unsubscribe?.();
    unsubscribe = null;
    stream?.stop();
    stream = null;
  };

  const setSymbols = (next: readonly string[]) => {
    symbols = cap(next);
    debugKernel("socket subscribing", { symbols });
    stream?.setSymbols(symbols);
  };

  const onQuotes = (next: QuoteHandler) => {
    handler = next;
  };

  return {
    open,
    close,
    setSymbols,
    onQuotes,
  };
};
